import React, { memo, useEffect, useRef } from 'react'
import { TAppSubMenuItem, TPaginationParamsWithLoading } from '../analytics';
import { NONE, PREVIOS_PERIOD } from '../AnalyticsConstants';

const LoadingSpinner = ({ size = 5, className = "" }: {
    size?: number;
    className?: string;
}) => (
    <div
        className={`tw-animate-spin tw-border-2 tw-border-[#0052CC] tw-border-t-transparent tw-rounded-full ${className}`}
        style={{ width: `${size}px`, height: `${size}px` }}
    />
);

const SubMenuItem = memo(({
    title,
    onClick,
    isSelected,
    compareToMenu,
}: {
    title: string;
    onClick: () => void;
    isSelected: boolean;
    compareToMenu?: string;
}) => {
    const isRadioMode = compareToMenu === PREVIOS_PERIOD;

    return (
        <div className={`tw-form-check tw-pe-3 tw-ps-1 tw-justify-between hover:tw-bg-[#F0F3FE] hover:tw-text-[#091CAA]`}>
            <div className={`tw-flex tw-justify-between tw-items-center tw-py-1 tw-px-2 tw-w-auto`}>
                <button
                    onClick={onClick}
                    className="tw-flex tw-items-center tw-cursor-pointer"
                >
                    <input
                        type={isRadioMode ? 'radio' : 'checkbox'}
                        className="tw-accent-[#091CAA]"
                        checked={isSelected}
                        onChange={() => { }}
                    />
                    <label className="tw-ms-2 tw-text-[#172B4D]">
                        {title}
                    </label>
                </button>
            </div>
        </div>
    );
});

const LoadMoreButton = ({ onClick, isLoading }: {
    onClick: () => void;
    isLoading: boolean;
}) => (
    <div className="tw-px-4 tw-py-3 tw-border-t tw-border-[#EBECF0]">
        <button
            className="tw-w-full button-style"
            onClick={onClick}
            disabled={isLoading}
        >
            {isLoading ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                    <LoadingSpinner size={4} />
                    Loading...
                </div>
            ) : (
                'Load more'
            )}
        </button>
    </div>
);

const SubMenu = ({
    isOpen,
    items,
    pagination,
    onItemClick,
    onLoadMore,
    onSelectAll,
    onUnselectAll,
    allSubMenuItemsChecked,
    compareToMenu,
    selectedItems = [],
    onClose = () => { },
    isRadioMode = false,
    title
}: {
    isOpen: boolean;
    items: TAppSubMenuItem[];
    pagination: TPaginationParamsWithLoading;
    onItemClick: (item: TAppSubMenuItem) => void;
    onLoadMore: () => void;
    onSelectAll: () => void;
    onUnselectAll: () => void;
    allSubMenuItemsChecked: boolean;
    compareToMenu: string;
    selectedItems?: TAppSubMenuItem[];
    onClose?: () => void;
    isRadioMode?: boolean;
    title: string
}) => {
    const prevIsOpenRef = useRef(isOpen);

    useEffect(() => {
        if (prevIsOpenRef.current && !isOpen) {
            onClose();
        }
        prevIsOpenRef.current = isOpen;
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="tw-absolute tw-right-full tw-top-0 tw-mr-0.5">
            <div className="tw-bg-white tw-rounded-[4px] tw-ring-1 tw-ring-[#EBECF0] tw-shadow-lg tw-w-max">
                {!items?.length ? null : (
                    <div className="tw-flex tw-items-center tw-gap-3 tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-[#EBECF0]">
                        <span className="tw-text-sm tw-font-medium tw-text-[#172B4D]">{title}</span>
                        {compareToMenu === NONE && !isRadioMode && (
                            <button
                                onClick={allSubMenuItemsChecked ? onUnselectAll : onSelectAll}
                                disabled={!items?.length}
                                className={`tw-text-sm ${items?.length ? 'tw-text-[#0052CC] hover:tw-text-[#0747A6]' : 'tw-text-gray-400 tw-cursor-not-allowed'}`}
                            >
                                {allSubMenuItemsChecked ? "Unselect all" : "Select all"}
                            </button>
                        )}
                    </div>
                )}

                <div className="tw-max-h-[300px] tw-overflow-y-auto">
                    {pagination.loading && pagination.pageNumber === 1 ? (
                        <div className="tw-px-4 tw-py-6 tw-flex tw-justify-center tw-items-center">
                            <LoadingSpinner size={5} />
                        </div>
                    ) : !items?.length ? (
                        <div className="tw-px-4 tw-py-6 tw-text-sm tw-text-gray-500 tw-text-center">
                            No items found
                        </div>
                    ) : (
                        items.map((item) => (
                            <SubMenuItem
                                key={item.value}
                                title={item.title}
                                onClick={() => onItemClick(item)}
                                isSelected={selectedItems.some((e: TAppSubMenuItem) => e.value === item.value)}
                                compareToMenu={compareToMenu}
                            />
                        ))
                    )}
                </div>

                {pagination.hasMore && items?.length !== 0 && (
                    <div className="tw-border-t tw-border-[#EBECF0]">
                        <LoadMoreButton
                            isLoading={pagination.loading}
                            onClick={onLoadMore}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubMenu;